import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { Package, CheckCircle2, Truck, Home, XCircle, Loader2, MapPin } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL || ''}/api`;

const ADIMLAR = [
  { key: 1, label: 'Sipariş alındı', alt: 'Siparişiniz sisteme alındı', icon: Package },
  { key: 2, label: 'Onaylandı', alt: 'Siparişiniz onaylandı, hazırlanıyor', icon: CheckCircle2 },
  { key: 3, label: 'Yola çıktı', alt: 'Siparişiniz kargoya verildi', icon: Truck },
  { key: 4, label: 'Teslim edildi', alt: 'Siparişiniz teslim edildi', icon: Home },
];

function aktifAdim(d) {
  if (d.iptal) return -1;
  if (d.kargo_teslim_edildi) return 4;
  const durumLow = (d.durum || '').toLowerCase();
  if (d.yola_cikti_tarihi || durumLow === 'tamamlandı' || durumLow === 'tamamlandi') return 3;
  if (d.onay_tarihi || durumLow === 'onaylandi' || durumLow === 'hazirlaniyor') return 2;
  return 1;
}

const DURUM_PILL = {
  '-1': { t: 'İptal edildi', c: 'bg-red-100 text-red-700' },
  1: { t: 'Onay bekliyor', c: 'bg-slate-100 text-slate-600' },
  2: { t: 'Hazırlanıyor', c: 'bg-amber-100 text-amber-700' },
  3: { t: 'Yola çıktı', c: 'bg-blue-100 text-blue-700' },
  4: { t: 'Teslim edildi', c: 'bg-emerald-100 text-emerald-700' },
};

const fmt = (t) => {
  if (!t) return null;
  try { return new Date(t).toLocaleString('tr-TR', { day: '2-digit', month: 'long', hour: '2-digit', minute: '2-digit' }); }
  catch { return null; }
};

const LogoImg = ({ className = 'h-9' }) => (
  <img
    src="/yetsan-logo-yatay.png" alt="YETSAN"
    className={`${className} w-auto object-contain mx-auto`}
    onError={(e) => { e.target.onerror = null; e.target.src = '/yetsan-logo.png'; }}
  />
);

export default function SiparisTakip() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hata, setHata] = useState('');

  useEffect(() => {
    (async () => {
      try {
        const r = await axios.get(`${API}/takip/${token}`);
        if (r.data?.success) setData(r.data);
        else setHata(r.data?.error || 'Sipariş bulunamadı');
      } catch (e) { setHata('Sipariş bilgisi alınamadı'); }
      setLoading(false);
    })();
  }, [token]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-red-50 flex items-center justify-center">
        <Loader2 className="animate-spin text-red-500" size={30} />
      </div>
    );
  }

  if (hata || !data) {
    return (
      <div className="min-h-screen bg-gradient-to-b from-slate-50 to-red-50 flex items-center justify-center p-6">
        <div className="bg-white rounded-3xl shadow-lg border border-slate-100 p-8 text-center max-w-sm w-full">
          <LogoImg />
          <XCircle className="mx-auto text-red-400 mt-5 mb-3" size={40} />
          <h1 className="text-lg font-bold text-slate-800">Takip bulunamadı</h1>
          <p className="text-sm text-slate-500 mt-1">{hata || 'Bu takip kodu geçersiz olabilir.'}</p>
        </div>
      </div>
    );
  }

  const adim = aktifAdim(data);
  const iptal = data.iptal;
  const pill = DURUM_PILL[iptal ? '-1' : adim] || DURUM_PILL[1];

  return (
    <div className="min-h-screen bg-gradient-to-b from-slate-50 via-red-50/40 to-slate-50 pb-12">
      {/* Üst bar — logo */}
      <div className="bg-white border-b border-slate-100 shadow-sm">
        <div className="max-w-md mx-auto px-5 py-4">
          <LogoImg />
          <p className="text-center text-[11px] tracking-widest uppercase text-slate-400 mt-2">Sipariş Takibi</p>
        </div>
      </div>

      <div className="max-w-md mx-auto px-4 mt-5 space-y-4">
        {/* Sipariş kartı */}
        <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
          <div className="flex items-center justify-between">
            <div>
              <div className="text-[11px] uppercase tracking-wide text-slate-400">Sipariş No</div>
              <div className="text-lg font-bold font-mono text-slate-800 mt-0.5">{data.siparis_no || '-'}</div>
              {data.firma_unvan && <div className="text-xs text-slate-500 mt-1">{data.firma_unvan}</div>}
            </div>
            <span className={`text-xs font-bold px-3 py-1.5 rounded-full ${pill.c}`}>{pill.t}</span>
          </div>
        </div>

        {/* İptal / Durum çizelgesi */}
        {iptal ? (
          <div className="bg-red-50 border border-red-200 rounded-3xl p-6 text-center">
            <XCircle className="mx-auto text-red-500 mb-2" size={38} />
            <h2 className="font-bold text-red-700">Sipariş iptal edildi</h2>
            {data.iptal_nedeni && <p className="text-sm text-red-600 mt-1">{data.iptal_nedeni}</p>}
          </div>
        ) : (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-6">
            {ADIMLAR.map((a, i) => {
              const tamam = adim >= a.key;
              const aktif = adim === a.key;
              const gecti = adim > a.key;
              const Icon = a.icon;
              const sonAdim = i === ADIMLAR.length - 1;
              let zaman = null;
              if (a.key === 1) zaman = fmt(data.created_at);
              if (a.key === 2) zaman = fmt(data.onay_tarihi);
              if (a.key === 3) zaman = fmt(data.yola_cikti_tarihi || data.hazirlama_bitis);
              if (a.key === 4) zaman = fmt(data.kargo_teslim_tarihi);
              return (
                <div key={a.key} className="flex gap-4">
                  <div className="flex flex-col items-center">
                    <div className={`relative w-11 h-11 rounded-full flex items-center justify-center transition-all duration-300 ${
                      tamam ? 'bg-gradient-to-br from-emerald-500 to-green-600 text-white shadow-md shadow-emerald-200' : 'bg-slate-100 text-slate-300'
                    }`}>
                      <Icon size={19} />
                      {aktif && <span className="absolute inset-0 rounded-full ring-4 ring-emerald-200 animate-pulse" />}
                    </div>
                    {!sonAdim && <div className={`w-1 flex-1 min-h-[34px] rounded-full my-1 ${gecti ? 'bg-emerald-400' : 'bg-slate-100'}`} />}
                  </div>
                  <div className={`flex-1 ${sonAdim ? 'pb-0' : 'pb-6'}`}>
                    <div className={`font-bold text-[15px] ${tamam ? 'text-slate-800' : 'text-slate-300'}`}>{a.label}</div>
                    <div className={`text-xs mt-0.5 ${tamam ? 'text-slate-500' : 'text-slate-300'}`}>{a.alt}</div>
                    {zaman && tamam && <div className="text-[11px] text-emerald-600 font-medium mt-1">{zaman}</div>}
                    {aktif && !zaman && <div className="text-[11px] text-emerald-600 font-semibold mt-1">● Şu an bu aşamada</div>}
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* Kargo bilgisi */}
        {!iptal && (data.kargo_firmasi || data.kargo_takip_no) && (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
              <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center"><Truck size={16} className="text-blue-500" /></div>
              Kargo Bilgisi
            </div>
            <div className="space-y-1.5 pl-1">
              {data.kargo_firmasi && (
                <div className="flex justify-between text-sm"><span className="text-slate-500">Firma</span><span className="font-semibold text-slate-800">{data.kargo_firmasi}</span></div>
              )}
              {data.kargo_takip_no && (
                <div className="flex justify-between text-sm"><span className="text-slate-500">Takip No</span><span className="font-mono font-semibold text-slate-800">{data.kargo_takip_no}</span></div>
              )}
            </div>
          </div>
        )}

        {/* Ürünler */}
        {data.urunler?.length > 0 && (
          <div className="bg-white rounded-3xl shadow-sm border border-slate-100 p-5">
            <div className="flex items-center gap-2 text-sm font-bold text-slate-700 mb-3">
              <div className="w-8 h-8 rounded-full bg-red-50 flex items-center justify-center"><Package size={16} className="text-red-500" /></div>
              Ürünler <span className="text-slate-400 font-normal">({data.urunler.length})</span>
            </div>
            <div className="space-y-3">
              {data.urunler.map((u, i) => (
                <div key={i} className="flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <div className="text-sm text-slate-800 truncate">{u.urun_adi || u.sku || 'Ürün'}</div>
                    {u.sku && <div className="text-[11px] text-slate-400 font-mono mt-0.5">{u.sku}</div>}
                  </div>
                  <div className="shrink-0 bg-slate-100 text-slate-700 text-xs font-bold px-3 py-1 rounded-full">{u.adet} adet</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Footer */}
        <div className="text-center pt-2">
          <div className="inline-flex items-center gap-1.5 text-[11px] text-slate-400">
            <MapPin size={11} /> panel.otoradyator.com
          </div>
        </div>
      </div>
    </div>
  );
}
